import * as THREE from 'three';

// Simple video display shader
const videoShader = {
  uniforms: {
    tDiffuse: { value: null },
    aspect: { value: 1.0 },
    videoAspect: { value: 1.0 },
    scale: { value: new THREE.Vector2(1, 1) }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float aspect;
    uniform float videoAspect;
    uniform vec2 scale;
    varying vec2 vUv;

    vec2 coverScale(vec2 uv) {
      // Calculate the aspect ratio correction
      float aspectCorrection = aspect / videoAspect;
      
      // Center the UV coordinates
      vec2 centered = uv - 0.5;
      
      // Scale based on the larger dimension
      if (aspectCorrection > 1.0) {
        // Container is wider than video
        centered.x *= aspectCorrection;
      } else {
        // Container is taller than video
        centered.y /= aspectCorrection;
      }
      
      // Return to original space
      return centered + 0.5;
    }

    void main() {
      vec2 uv = vUv;
      gl_FragColor = texture2D(tDiffuse, coverScale(uv));
    }
  `
};

export default class VideoCanvas {
  constructor() {
    this.init();
  }

  init() {
    // Setup scene
    this.element = document.querySelector('#page-welcome');

    if (!this.element) {
      return;
    }

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.renderer = new THREE.WebGLRenderer();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    
    this.element.appendChild(this.renderer.domElement);

    // Select appropriate video based on device
    this.selectVideo();
    
    // Create video texture
    this.videoTexture = new THREE.VideoTexture(this.video);
    
    // Create video material
    this.videoMaterial = new THREE.ShaderMaterial({
      uniforms: {
        ...videoShader.uniforms,
        tDiffuse: { value: this.videoTexture },
        aspect: { value: window.innerWidth / window.innerHeight },
        videoAspect: { value: this.video.videoWidth / this.video.videoHeight },
        scale: { value: new THREE.Vector2(1, 1) }
      },
      vertexShader: videoShader.vertexShader,
      fragmentShader: videoShader.fragmentShader
    });

    this.calculateCoverScale();

    // Create plane for video display
    const geometry = new THREE.PlaneGeometry(2, 2);
    this.videoMesh = new THREE.Mesh(geometry, this.videoMaterial);
    
    this.scene.add(this.videoMesh);

    // Add resize listener
    window.addEventListener('resize', this.onResize.bind(this));

    this.animate();
    setTimeout(this.onResize.bind(this), 300);
  }

  selectVideo() {
    console.log('selectVideo');
    const isMobile = window.innerWidth <= 768;
    const desktopVideo = document.getElementById('desktop-video');
    const mobileVideo = document.getElementById('mobile-video');

    if (!desktopVideo && !mobileVideo) {
      return;
    }

    // Hide both videos initially
    desktopVideo.style.display = 'none';
    mobileVideo.style.display = 'none';

    // Select and show appropriate video
    this.video = isMobile ? mobileVideo : desktopVideo;
    
    // Add load event handler
    this.video.addEventListener('loadeddata', () => {
      console.log('loadeddata');
      this.onResize();
    });
    
    this.video.play();
  }

  calculateCoverScale() {
    const containerAspect = window.innerWidth / window.innerHeight;
    const videoAspect = this.video.videoWidth / this.video.videoHeight;
    
    let scaleX = 1;
    let scaleY = 1;
    
    if (containerAspect > videoAspect) {
      // Container is wider than video
      scaleX = containerAspect / videoAspect;
    } else {
      // Container is taller than video
      scaleY = videoAspect / containerAspect;
    }
    
    this.videoMaterial.uniforms.scale.value.set(scaleX, scaleY);
  }

  onResize() {
    console.log('onResize');
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.videoMaterial.uniforms.aspect.value = window.innerWidth / window.innerHeight;
    
    this.calculateCoverScale();

    // Check if we need to switch videos
    const isMobile = window.innerWidth <= 768;

    this.selectVideo();
    this.videoTexture = new THREE.VideoTexture(this.video);
    this.videoMaterial.uniforms.tDiffuse.value = this.videoTexture;
    this.videoMaterial.uniforms.videoAspect.value = this.video.videoWidth / this.video.videoHeight;
  }

  animate() {
    requestAnimationFrame(this.animate.bind(this));
    this.renderer.render(this.scene, this.camera);
  }
}
